import { BeakerIcon, PlusIcon } from 'lucide-react';
import { Link, useParams } from 'react-router';

import { Button } from '@/components/core/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';
import { DetailGroup } from '@/components/detail-group';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, DetailSide, Page, PageContent } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import type { SimulationDTO, SimulationScenarioDTO } from '@/gateways/impl/SimulationGateway';
import { useEntityDetailsQuery } from '@/shared/hooks/queries';

import {
  canCreateScenarioForInputsStatus,
  disabledScenarioReason,
  isTerminalRunStatus,
  TERMINAL_INPUTS_STATUSES,
} from '../shared/inputs-status';
import { SimulationDetailsSection } from './simulation-details-section';
import { SimulationScenariosCard } from './simulation-scenarios-card';

type SimulationDetailsData = {
  id: string;
  simulation: SimulationDTO;
  scenarios: SimulationScenarioDTO[];
};

export function SimulationDetails() {
  const { id } = useParams<{ id: string }>();
  const { simulationGateway } = useGateways();

  const { data, isLoading } = useEntityDetailsQuery<SimulationDetailsData>({
    parentKey: 'simulations',
    id,
    isNew: !id,
    query: async (fetchId, { signal }) => {
      const simulationDetails = await simulationGateway.getSimulation(fetchId, { signal });
      return { id: simulationDetails.simulation.id, ...simulationDetails };
    },
    refetchInterval: ({ state }) => {
      const current = state.data;
      if (!current) return 2000;
      if (!TERMINAL_INPUTS_STATUSES.has(current.simulation.inputsStatus)) return 2000;
      if (current.simulation.inputsStatus === 'FAILED') return false;
      return current.scenarios.some((s) => !isTerminalRunStatus(s.runStatus)) ? 2000 : false;
    },
  });

  const simulation = data?.simulation;
  const scenarios = data?.scenarios ?? [];

  const headerTitle = simulation?.name ?? 'Simulation';
  const canCreateScenario = canCreateScenarioForInputsStatus(simulation?.inputsStatus);
  const newScenarioTo = `/simulations/${id}/scenarios/new`;

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon parentUrl="/simulations" parentTitle="Simulations">
          <BeakerIcon />
        </HeaderIcon>
        <HeaderTitle>{headerTitle}</HeaderTitle>
        <HeaderActions>
          {canCreateScenario ? (
            <Button asChild disabled={isLoading}>
              <Link to={newScenarioTo}>
                <PlusIcon />
                New scenario
              </Link>
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button disabled aria-disabled>
                    <PlusIcon />
                    New scenario
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{disabledScenarioReason(simulation?.inputsStatus)}</TooltipContent>
            </Tooltip>
          )}
        </HeaderActions>
      </Header>
      <PageContent variant="split">
        <DetailMain>
          <SimulationScenariosCard
            simulationId={id}
            scenarios={scenarios}
            isLoading={isLoading}
            canCreateScenario={canCreateScenario}
            disabledReason={disabledScenarioReason(simulation?.inputsStatus)}
          />
        </DetailMain>
        <DetailSide>
          <DetailGroup>
            <SimulationDetailsSection simulation={simulation ?? {}} isLoading={isLoading} />
          </DetailGroup>
        </DetailSide>
      </PageContent>
    </Page>
  );
}
